// src/app/time-attendance/components/payroll-sync/pages/payroll-sync-history/payroll-sync-history.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';

import { PayrollSyncService } from '../services/payroll-sync.service';
import { PayableHoursRunHistoryDto } from '../models/payroll-sync.model';
import { PayrollNavComponent } from '../../../shared/payroll-nav/payroll-nav.component';

type ToastKind = 'success' | 'error' | 'info';

@Component({
    selector: 'app-payroll-sync-history',
    standalone: true,
    imports: [CommonModule, DatePipe, DecimalPipe, PayrollNavComponent],
    templateUrl: './payroll-sync-history.component.html',
    styleUrls: ['./payroll-sync-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollSyncHistoryComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    isLoading = true;
    errorMessage: string | null = null;

    toastMessage: string | null = null;
    toastKind: ToastKind = 'info';
    private toastTimer: ReturnType<typeof setTimeout> | null = null;

    historyRows: PayableHoursRunHistoryDto[] = [];

    pageNumber = 1;
    pageSize = 10;
    totalCount = 0;
    totalPages = 1;

    constructor(
        private readonly payrollSyncSvc: PayrollSyncService,
        private readonly cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        void this.loadHistory();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearToast();
    }

    async loadHistory(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;
        this.cdr.markForCheck();

        try {
            const response = await firstValueFrom(
                this.payrollSyncSvc.getRunHistory({
                    pageNumber: this.pageNumber,
                    pageSize: this.pageSize,
                }),
            );

            this.historyRows = response.data ?? [];
            this.totalCount = response.totalCount ?? 0;
            this.totalPages = response.totalPages ?? 1;
            this.pageNumber = response.currentPage ?? this.pageNumber;
            this.pageSize = response.pageSize ?? this.pageSize;
        } catch (error) {
            console.error('getRunHistory error', error);
            this.errorMessage = 'Failed to load payroll sync history.';
            this.historyRows = [];
            this.totalCount = 0;
            this.totalPages = 1;
            this.showToast('error', 'Failed to load payroll sync history.');
        } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    }

    refresh(): void {
        void this.loadHistory();
    }

    previousPage(): void {
        if (this.pageNumber <= 1 || this.isLoading) {
            return;
        }

        this.pageNumber -= 1;
        void this.loadHistory();
    }

    nextPage(): void {
        if (this.pageNumber >= this.totalPages || this.isLoading) {
            return;
        }

        this.pageNumber += 1;
        void this.loadHistory();
    }

    onPageSizeChange(raw: string): void {
        const nextPageSize = Number(raw);

        if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
            return;
        }

        this.pageSize = nextPageSize;
        this.pageNumber = 1;
        void this.loadHistory();
    }

    getCreatedBy(row: PayableHoursRunHistoryDto): string {
        const firstName = row.createdBy?.firstName ?? '';
        const lastName = row.createdBy?.lastName ?? '';

        return `${firstName} ${lastName}`.trim() || '—';
    }

    clearToast(): void {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = null;
        this.toastMessage = null;
        this.cdr.markForCheck();
    }

    private showToast(kind: ToastKind, message: string): void {
        this.toastKind = kind;
        this.toastMessage = message;

        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = setTimeout(() => this.clearToast(), 2600);
        this.cdr.markForCheck();
    }

    trackById(_: number, item: { id: number }): number {
        return item.id;
    }
}